import { create } from 'zustand';

export const useCameraStore = create((set) => {
  async function getCameraInfos(deviceId) {
    /*
            Get camera resolution and direction.
            To do this, a short stream needs to be opened -> This can disrupt a running stream!
            */
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
      });
      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities ? track.getCapabilities() : {};
      track.stop();

      const cameraInfo = {
        resolution: Math.min(capabilities.width?.max ?? 1920, capabilities.height?.max ?? 1920),
        resolution_width: capabilities.width?.max ?? 1920,
        resolution_height: capabilities.height?.max ?? 1920,
        aspect_ratio: (capabilities.width?.max ?? 1920) / (capabilities.height?.max ?? 1920),
        facingMode: capabilities.facingMode ?? [],
      };
      return cameraInfo;
    } catch (err) {
      console.warn(`Could not get camera info for device ${deviceId}:`, err);
      return null;
    }
  }

  async function getDevices() {
    /*
            Get all available camera devices and load their infos
            Only pick cameras facing the back of the device.
            Request permission with facingMode 'environment' first to ensure
            the back camera is primed and permissions are granted before enumerating.
            */

    // Request back camera permission first — this ensures enumerateDevices
    // returns full device info on iOS 26+ / Safari 26+
    let permissionStream = null;
    try {
      permissionStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
    } catch (err) {
      // Fall back to any camera if back camera request fails
      try {
        permissionStream = await navigator.mediaDevices.getUserMedia({ video: true });
      } catch (fallbackErr) {
        console.error('Could not access any camera:', fallbackErr);
        return {};
      }
    }
    // Stop the permission stream — we only needed it to grant access
    permissionStream.getTracks().forEach((track) => track.stop());

    const devices = await navigator.mediaDevices.enumerateDevices();
    let filteredDevices = devices.filter((device) => device.kind === 'videoinput');

    let cameraInfosNew = {};
    for (const device of filteredDevices) {
      const info = await getCameraInfos(device.deviceId);
      if (info) {
        cameraInfosNew[device.deviceId] = { label: device.label, ...info };
      }
    }

    // Only keep cameras facing the back of the device (unless there are none)
    const cameraInfosFilteredBack = Object.entries(cameraInfosNew).reduce((cameraInfo, [key, value]) => {
      if (Array.isArray(value.facingMode) && value.facingMode.indexOf('environment') > -1) {
        cameraInfo[key] = value;
      }
      return cameraInfo;
    }, {});
    if (Object.keys(cameraInfosFilteredBack).length > 0) {
      cameraInfosNew = cameraInfosFilteredBack;
    }
    set((state) => ({ ...state, cameraDevices: cameraInfosNew }));
    return cameraInfosNew;
  }

  return {
    cameraDevices: {},
    cameraInit: getDevices,
  };
});
